import { format } from "date-fns";
import { Pin } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { MarkdownReact } from "@/components/MarkdownReact";
import { UserAvatar } from "@/components/UserAvatar";
import { OnboardingAnnouncement, PublicUser } from "@forgepoint/shared";
import { getUserDisplayName, getUserInitials } from "@/store/teamsStore";

interface AnnouncementItemProps {
  announcement: OnboardingAnnouncement;
  author: PublicUser | undefined;
}

/**
 * One announcement post: the author's avatar on the left, then the header
 * (pin, name, Forgepoint badge, date) and the markdown body.
 */
export function AnnouncementItem({
  announcement,
  author,
}: AnnouncementItemProps) {
  const authorName = author ? getUserDisplayName(author) : "Forgepoint";

  return (
    <div className="flex gap-3 rounded-md border bg-muted/30 p-3">
      <UserAvatar
        className="mt-0.5 shrink-0"
        profilePictureUrl={author?.profilePictureUrl}
        name={authorName}
        initials={author ? getUserInitials(author) : "FP"}
      />
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2 text-xs text-muted-foreground">
          {announcement.pinned && <Pin className="h-3 w-3" />}
          <span className="font-medium text-foreground">{authorName}</span>
          {announcement.isForgepointAuthor && (
            <Badge variant="outline" className="border-primary/40 text-primary">
              Forgepoint
            </Badge>
          )}
          <span>{format(announcement.createdAt, "MMM d, yyyy")}</span>
        </div>
        <div className="prose prose-sm mt-2 max-w-none dark:prose-invert">
          <MarkdownReact>{announcement.body}</MarkdownReact>
        </div>
      </div>
    </div>
  );
}
